import React, { useState } from 'react';
import { VIDEOS, AGENCIES } from '../mock';
import { Sentences } from '../lib/text';
import { Play, X, Calendar, MapPin, Filter, Download } from 'lucide-react';

// Liens de téléchargement direct vers les fichiers officiels Navy (FOIA)
const NAVY_DOWNLOADS = {
  V001: 'https://www.navair.navy.mil/foia/sites/g/files/jejdrs566/files/2020-04/1%20FLIR.mp4',
  V002: 'https://www.navair.navy.mil/foia/sites/g/files/jejdrs566/files/2020-04/3%20GOFAST.wmv',
  V003: null,
  V004: null,
  V005: null,
  V006: null,
  V007: null,
  V008: null,
};

export default function VideosPage() {
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState('ALL');

  const filtered = filter === 'ALL' ? VIDEOS : VIDEOS.filter(v => v.tags.includes(filter));

  const allTags = Array.from(new Set(VIDEOS.flatMap(v => v.tags)));

  return (
    <div>
      <div className="relative border-b border-white/5">
        <div className="absolute inset-0 stars-bg twinkle" />
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative mx-auto max-w-[1400px] px-6 py-12">
          <div className="mono text-[11px] tracking-[0.22em] text-emerald-300/90 uppercase mb-3">Vidéothèque · Captures officielles</div>
          <h1 className="serif text-4xl md:text-5xl">Vidéos <span className="italic text-emerald-300">déclassifiées</span> · lecteur intégré</h1>
          <p className="text-2 mt-3 max-w-3xl">FLIR1, GIMBAL, GOFAST, témoignages au Congrès américain et reportages clés. Toutes les vidéos sont issues de sources publiques.</p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="mono text-[10px] tracking-widest text-3 uppercase inline-flex items-center gap-1.5"><Filter className="h-3 w-3" /> Thèmes</span>
            <button onClick={()=>setFilter('ALL')} className={`mono text-[11px] px-2.5 py-1 rounded border ${filter==='ALL' ? 'bg-cyan-400/15 border-cyan-300/40 text-cyan-100' : 'border-white/10 text-2 hover:bg-white/5'}`}>Toutes</button>
            {allTags.map(t => (
              <button key={t} onClick={()=>setFilter(t)} className={`mono text-[11px] px-2.5 py-1 rounded border ${filter===t ? 'bg-cyan-400/15 border-cyan-300/40 text-cyan-100' : 'border-white/10 text-2 hover:bg-white/5'}`}>{t}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 py-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(v => {
            const a = AGENCIES.find(x => x.id === v.agency);
            return (
              <button onClick={() => setActive(v)} key={v.id} className="text-left doc-card glass rounded-xl overflow-hidden group">
                <div className="relative aspect-video bg-black">
                  <img src={v.thumbnail} alt={v.title} className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity" onError={(e)=>{e.target.style.display='none';}} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-14 w-14 rounded-full bg-cyan-400/20 backdrop-blur-md border border-cyan-300/60 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="h-6 w-6 text-cyan-100 ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="badge-classif" style={{ color: a?.color }}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: a?.color }} /> {a?.short}
                    </span>
                  </div>
                  <div className="absolute bottom-3 right-3 mono text-[11px] bg-black/70 text-1 px-2 py-1 rounded">{v.duration}</div>
                </div>
                <div className="p-4">
                  <div className="text-1 font-medium leading-snug mb-2">{v.title}</div>
                  <p className="text-3 text-sm line-clamp-2">{v.description}</p>
                  <div className="flex items-center gap-4 mt-3 mono text-[11px] text-3">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {v.date}</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {v.location}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Modal player */}
      {active && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center px-4 pt-4 pb-24 overflow-y-auto" onClick={()=>setActive(null)}>
          <div className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()}>
            <button onClick={()=>setActive(null)} className="absolute -top-12 right-0 text-2 hover:text-1 inline-flex items-center gap-2 text-sm">
              Fermer <X className="h-4 w-4" />
            </button>
            <div className="aspect-video bg-black rounded-xl overflow-hidden border border-white/10">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${active.youtubeId}?autoplay=1&cc_load_policy=1&cc_lang_pref=fr`}
                title={active.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="mt-3 glass rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1">
                  <div className="text-1 font-medium">{active.title}</div>
                  <Sentences text={active.description} className="text-2 text-sm mt-1" />
                  <div className="mono text-[11px] text-3 mt-2 flex flex-wrap gap-3">
                    <span>{active.date}</span><span>·</span><span>{active.location}</span><span>·</span><span>{active.duration}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <a href={`https://www.youtube.com/watch?v=${active.youtubeId}`} target="_blank" rel="noreferrer"
                     className="btn-ghost px-2.5 py-1 rounded text-[11px] inline-flex items-center gap-1">
                    <Play className="h-3 w-3" /> Voir sur YouTube
                  </a>
                  {NAVY_DOWNLOADS[active.id] && (
                    <a href={NAVY_DOWNLOADS[active.id]} target="_blank" rel="noreferrer"
                       className="btn-primary px-2.5 py-1 rounded text-[11px] inline-flex items-center gap-1">
                      <Download className="h-3 w-3" /> Fichier Navy officiel
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
